import React, { useState } from 'react';
import { Flag, X } from 'lucide-react';
import reportService from '../../services/reportService';
import { useAuth } from '../../context/AuthContext';
import { useFeedback } from '../../contexts/FeedbackContext';

const REPORT_REASONS = [
  { value: 'spam', label: 'Spam or repetitive content' },
  { value: 'harassment', label: 'Harassment or abusive behavior' },
  { value: 'scam', label: 'Scam or fraudulent activity' },
  { value: 'inappropriate_content', label: 'Inappropriate or offensive content' },
  { value: 'fake_followers', label: 'Fake followers or engagement' },
  { value: 'copyright', label: 'Copyright or intellectual property violation' },
  { value: 'other', label: 'Other (please describe)' },
];

const ReportFlag = ({ targetType, targetId, targetName, triggerClassName = '', triggerLabel = 'Report', buttonStyle = 'icon' }) => {
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const [showModal, setShowModal] = useState(false);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      showError('Authentication required.', 'Please log in to submit a report.', 4000);
      return;
    }
    if (!reason) {
      showError('Reason required.', 'Please select a reason for your report.', 4000);
      return;
    }
    if (!description.trim()) {
      showError('Description required.', 'Please provide details about the issue.', 4000);
      return;
    }

    setSubmitting(true);
    try {
      const reportType = targetType === 'campaign' ? 'campaign' : 'user';
      const result = await reportService.createReport(
        currentUser.uid,
        reportType,
        targetType,
        targetId,
        reason,
        description.trim()
      );

      if (result === null) {
        showError('Already reported.', 'You have already submitted a pending report for this target.', 4000);
      } else {
        showSuccess('Report submitted.', 'Our team will review your report and take appropriate action.', 5000);
        setShowModal(false);
        setReason('');
        setDescription('');
      }
    } catch (err) {
      showError('Failed to submit report.', 'Please try again later.', 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setReason('');
    setDescription('');
  };

  return (
    <>
      {buttonStyle === 'icon' ? (
        <button
          type="button"
          onClick={() => setShowModal(true)}
          className={`inline-flex items-center text-gray-400 hover:text-red-600 transition-colors ${triggerClassName}`}
          title="Report"
          aria-label="Report"
        >
          <Flag className="h-4 w-4" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setShowModal(true)}
          className={`inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600 transition-colors ${triggerClassName}`}
        >
          <Flag className="h-4 w-4" />
          {triggerLabel}
        </button>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Submit a Report</h3>
              <button type="button" onClick={handleClose} className="text-gray-400 hover:text-gray-500 transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {targetName && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Reporting:</p>
                  <p className="text-sm font-medium text-gray-900">{targetName}</p>
                </div>
              )}

              <div>
                <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
                  Reason
                </label>
                <select
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  required
                  className="block w-full border border-gray-300 rounded-md py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition duration-150 ease-in-out"
                >
                  <option value="">Select a reason...</option>
                  {REPORT_REASONS.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  rows={4}
                  className="block w-full border border-gray-300 rounded-md py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition duration-150 ease-in-out"
                  placeholder="Provide detailed information about the issue..."
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-700">
                  False reports may result in account restrictions. Only report genuine violations of our community guidelines.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={handleClose} className="inline-flex justify-center py-2.5 px-6 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={submitting} className="inline-flex justify-center py-2.5 px-6 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {submitting ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportFlag;
